import { useState } from "react";
import { Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CompteSelect } from "@/components/CompteSelect";
import { PetitSelect } from "@/components/OperationBlock";
import { api, errMsg } from "@/lib/api";

const VIDE = { compte: "", categorie: "", logement: "", non_taxable: "inchange", commentaire: "" };

export const LotBar = ({ ids, comptes, categories, logements, onDone, onClear }) => {
  const [f, setF] = useState(VIDE);
  const [busy, setBusy] = useState(false);
  const champs = Object.fromEntries(Object.entries(f).filter(([k, v]) => (k === "non_taxable" ? v !== "inchange" : v.trim())));
  if (champs.non_taxable) champs.non_taxable = champs.non_taxable === "oui";
  const appliquer = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/transactions/commentaire-lot", { ids, champs });
      toast.success(`${data.modifiees} opération(s) mise(s) à jour et mémorisée(s)`);
      setF(VIDE); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="sticky top-2 z-20 mb-3 rounded-lg border border-amber-300 bg-amber-50/95 backdrop-blur p-3 shadow-sm" data-testid="lot-bar">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-semibold text-amber-900" data-testid="lot-count">{ids.length} opération{ids.length > 1 ? "s" : ""} cochée{ids.length > 1 ? "s" : ""} — laissez vide ce qui ne change pas</p>
        <Button size="sm" variant="ghost" onClick={onClear} data-testid="lot-clear"><X className="h-4 w-4 mr-1" />Décocher tout</Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-[2fr_1fr_1.3fr_1fr] gap-2">
        <CompteSelect value={f.compte} comptes={comptes} onChange={(compte) => setF({ ...f, compte })} testId="lot-compte" />
        <PetitSelect value={f.categorie} options={categories} placeholder="Catégorie" onChange={(categorie) => setF({ ...f, categorie })} testId="lot-categorie" />
        <PetitSelect parImmeuble avecImmeuble value={f.logement} options={logements} placeholder="Immeuble / logement" onChange={(logement) => setF({ ...f, logement })} testId="lot-logement" />
        <Select value={f.non_taxable} onValueChange={(non_taxable) => setF({ ...f, non_taxable })}>
          <SelectTrigger className="h-9 bg-white" data-testid="lot-non-taxable"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="inchange">Non taxable : inchangé</SelectItem>
            <SelectItem value="oui">Non taxable : oui</SelectItem>
            <SelectItem value="non">Non taxable : non</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="mt-2 flex flex-col sm:flex-row gap-2">
        <Input value={f.commentaire} onChange={(e) => setF({ ...f, commentaire: e.target.value })} placeholder="Commentaire (optionnel)" className="h-9 bg-white" data-testid="lot-commentaire" />
        <Button onClick={appliquer} disabled={busy || !Object.keys(champs).length} className="h-9 bg-emerald-700 hover:bg-emerald-800 whitespace-nowrap" data-testid="lot-appliquer">
          {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Appliquer aux opérations cochées
        </Button>
      </div>
    </div>
  );
};
